import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actionSendHttpRequest } from '@/redux/action';
import AllActionType from '@/redux/actionType';
import { api } from '@/fetch/bussiness/api';
import { RootState } from '@/redux/store';
import {
  RoadTestTaskType,
  VehicleOwnerUseCase,
} from '@/fetch/bussiness/roadtest/types';
import { RoadTestMode } from './utils';
import { useRoadTestTaskDetail } from './hooks/useRoadTestTaskDetail';
import RoadTestEntry from './RoadTestEntry';
import RoadTestDrawerShell from './RoadTestDrawerShell';
import CreateTaskDrawer from './CreateTaskDrawer';
import EvaluationDrawer from './EvaluationDrawer';
import './index.scss';

export interface RoadTestContainerProps {
  vehicleName: string;
  roadTestMode?: RoadTestMode;
  initialTaskNo?: string;
  initialTaskType?: RoadTestTaskType;
}

/**
 * 路测抽屉的实际内容组件
 *
 * @param {*} {
 *   vehicleName,
 *   roadTestMode,
 *   initialTaskNo,
 *   initialTaskType,
 * }
 * @return {*}
 */
const RoadTestContainer: React.FC<RoadTestContainerProps> = ({
  vehicleName,
  roadTestMode,
  initialTaskNo,
  initialTaskType,
}) => {
  const dispatch = useDispatch();

  // 车辆的路测任务状态
  const roadTaskExist = useSelector(
    (state: RootState) =>
      state.singleVehiclePageInfo.vehicleBasicInfoNew?.roadTaskExist
  );
  // 车辆是否处于生产阶段
  const isOwnerProduct =
    useSelector(
      (state: RootState) =>
        state.singleVehiclePageInfo.vehicleBasicInfoNew?.ownerUseCase
    ) === VehicleOwnerUseCase.PRODUCT;

  // 抽屉的可见性
  const [visible, setVisible] = useState(false);

  // 抽屉的轮询状态（用于跟踪路测任务，判断任务是进行中还是未开始）
  const [pollingEnabled, setPollingEnabled] = useState(false);
  // 任务是否处于结束中态（用户点击作废后，等待后端确认任务结束）
  const [isTaskEnding, setIsTaskEnding] = useState(false);

  // 用户是否手动关闭过抽屉（用于轮询状态下，用户手动关闭后不再自动展开）
  const userClosedRef = useRef(false);
  // 车辆切换时，重置所有状态的标记（避免首次渲染时触发 useEffect 重置）
  const vehicleResetDidMountRef = useRef(false);
  // 记录上一次的 roadTaskExist 状态，用于判断任务状态变化
  const prevRoadTaskExistRef = useRef<boolean | undefined>(undefined);

  // 获取路测任务详情（轮询）
  const { taskDetail, finished } = useRoadTestTaskDetail(vehicleName, {
    enabled: pollingEnabled,
  });

  // 由 pollingEnabled 派生 mode：跟踪任务时显示评价，否则显示创建
  const mode = useMemo<RoadTestMode>(
    () => (pollingEnabled ? RoadTestMode.EVALUATE : RoadTestMode.CREATE),
    [pollingEnabled]
  );

  /**
   * 重新获取车辆基础信息（monitor/web/basic/vehicle接口），更新 ownerUseCase / roadTaskExist
   */
  const fetchVehicleBasicInfo = useCallback(() => {
    if (!vehicleName) return;
    const data: HTTPFetchActionParams = {
      method: 'POST',
      path: `${api.getVehicleInfo}`,
      body: { vehicleName },
      actionType: AllActionType.MONITOR_VEHICLE_BASE_INFO,
    };
    dispatch(actionSendHttpRequest(data));
  }, [dispatch, vehicleName]);

  /**
   * 页面恢复可见时刷新车辆基础信息（仅 PRODUCT 车辆）
   */
  useEffect(() => {
    if (!isOwnerProduct) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchVehicleBasicInfo();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchVehicleBasicInfo, isOwnerProduct]);

  /**
   * -----url初始化状态-----
   * URL 初始化：直接根据 URL 参数决定模式
   */
  useEffect(() => {
    // 创建模式：直接展开抽屉，关闭轮询，重置结束中态
    if (roadTestMode === RoadTestMode.CREATE) {
      setVisible(true);
      setPollingEnabled(false);
      setIsTaskEnding(false);
      userClosedRef.current = false;
      return;
    }
    // 评价模式：如果 URL 中有 taskNo 和 taskType，则直接展开抽屉，开启轮询，重置结束中态
    if (
      roadTestMode === RoadTestMode.EVALUATE &&
      initialTaskNo &&
      initialTaskType
    ) {
      setVisible(true);
      setPollingEnabled(true);
      setIsTaskEnding(false);
      userClosedRef.current = false;
    }
  }, [roadTestMode, initialTaskNo, initialTaskType]);

  /**
   * -----切车状态-----
   * 切换车辆后重置所有状态
   */
  useEffect(() => {
    // 避免首次渲染时触发 useEffect 重置
    if (!vehicleResetDidMountRef.current) {
      vehicleResetDidMountRef.current = true;
      return;
    }
    // 车辆切换后重置所有状态
    setVisible(false);
    setPollingEnabled(false);
    setIsTaskEnding(false);
    userClosedRef.current = false;
  }, [vehicleName]);

  /**
   * -----任务状态-----
   * 当 roadTaskExist 从 false 变为 true 时，开启轮询；从 true 变为 false 时，停止轮询。
   */
  useEffect(() => {
    if (roadTaskExist && !prevRoadTaskExistRef.current) {
      setPollingEnabled(true);
      setIsTaskEnding(false);
    } else if (!roadTaskExist) {
      setPollingEnabled(false);
      setIsTaskEnding(false);
    }
    prevRoadTaskExistRef.current = roadTaskExist;
  }, [roadTaskExist]);

  /**
   * -----任务状态-----
   * 当任务完成（finished）时，停止轮询并关闭抽屉。
   */
  useEffect(() => {
    if (finished) {
      setPollingEnabled(false);
      setIsTaskEnding(false);
      setVisible(false);
      userClosedRef.current = false;
      // 任务结束后刷新车辆基础信息，确保 roadTaskExist 等状态同步
      fetchVehicleBasicInfo();
    }
  }, [finished, fetchVehicleBasicInfo]);

  /**
   * 控制抽屉的打开和关闭
   * 用户未手动关闭时，有任务自动展开；无任务时自动关闭
   */
  useEffect(() => {
    if (pollingEnabled && !userClosedRef.current) {
      setVisible(true);
    }
    if (!pollingEnabled) {
      setVisible(false);
      userClosedRef.current = false;
    }
  }, [pollingEnabled]);

  /**
   * -----交互回调-----
   * 用户点击创建任务后的回调：开启轮询，重置结束中态，标记用户未手动关闭
   */
  const handleCreated = useCallback(() => {
    setPollingEnabled(true);
    setIsTaskEnding(false);
    userClosedRef.current = false;
    fetchVehicleBasicInfo();
  }, [fetchVehicleBasicInfo]);

  /**
   * -----交互回调-----
   * 保存/提交成功后收起抽屉并刷新车辆基础信息；任务仍在进行时保留 toggle，用户可再次展开继续操作
   */
  const handleEvaluationActionSuccess = useCallback(() => {
    setVisible(false);
    userClosedRef.current = true;
    fetchVehicleBasicInfo();
  }, [fetchVehicleBasicInfo]);

  /**
   * -----交互回调-----
   * 用户点击作废后的回调：进入结束中态，继续轮询等待 finished/roadTaskExist=false
   */
  const handleCancel = useCallback(() => {
    setVisible(false);
    userClosedRef.current = true;
    setIsTaskEnding(true);
    fetchVehicleBasicInfo();
  }, [fetchVehicleBasicInfo]);

  /**
   * -----交互回调-----
   * 抽屉壳的关闭回调（点击mask或关闭X按钮）
   */
  const handleDrawerClose = useCallback(() => {
    setVisible(false);
    userClosedRef.current = true;
  }, []);

  /**
   * -----交互回调-----
   * 抽屉壳的收起/展开按钮点击回调
   */
  const handleToggle = useCallback(() => {
    setVisible((prev) => {
      const next = !prev;
      userClosedRef.current = !next;
      return next;
    });
  }, []);

  /**
   * 路测入口按钮点击回调：切换抽屉展开/收起
   */
  const handleEntryClick = useCallback(() => {
    if (visible) {
      setVisible(false);
      userClosedRef.current = true;
    } else {
      setVisible(true);
      userClosedRef.current = false;
    }
  }, [visible]);

  /**
   * 按钮可见性
   */
  const showToggle = pollingEnabled;
  const showEntryButton = !pollingEnabled;

  /**
   * 计算 Drawer 标题
   */
  const drawerTitle = useMemo(() => {
    if (mode === RoadTestMode.CREATE) return '创建路测任务';
    const typeName =
      taskDetail?.taskType === RoadTestTaskType.FATIGUE ? '疲劳' : '综合';
    const base = `测评-${vehicleName}-${typeName}`;
    if (taskDetail?.currentRoadName) {
      return (
        <div className="drawer-title-row">
          <span className="drawer-title-main">{base}</span>
          <span
            className="drawer-title-main drawer-title-road"
            data-testid="evaluation-current-road"
          >
            {taskDetail.currentRoadName}
          </span>
        </div>
      );
    }
    return base;
  }, [mode, vehicleName, taskDetail?.taskType, taskDetail?.currentRoadName]);

  return (
    <>
      {/* 路测任务入口 */}
      {showEntryButton && (
        <RoadTestEntry vehicleName={vehicleName} onClick={handleEntryClick} />
      )}
      {/* 路测抽屉 */}
      <RoadTestDrawerShell
        open={visible}
        onClose={handleDrawerClose}
        onToggle={handleToggle}
        showToggle={showToggle}
        title={drawerTitle}
        closable={mode === RoadTestMode.CREATE}
        mask={mode === RoadTestMode.CREATE}
      >
        {mode === RoadTestMode.CREATE ? (
          // 创建任务抽屉
          <CreateTaskDrawer
            vehicleName={vehicleName}
            onCreated={handleCreated}
            open={visible}
          />
        ) : (
          // 评价任务抽屉
          <EvaluationDrawer
            vehicleName={vehicleName}
            taskNo={taskDetail?.taskNo || initialTaskNo || ''}
            taskType={
              taskDetail?.taskType ||
              initialTaskType ||
              RoadTestTaskType.COMPREHENSIVE
            }
            taskDetail={taskDetail}
            onSaveSuccess={handleEvaluationActionSuccess}
            onSubmitSuccess={handleEvaluationActionSuccess}
            onCancel={handleCancel}
            isTaskEnding={isTaskEnding}
          />
        )}
      </RoadTestDrawerShell>
    </>
  );
};

export default React.memo(RoadTestContainer);
